import React, { useRef, useState } from 'react';
import { CommonBackContent } from '../Layouts/CommonBackContent';
import dynamic from 'next/dynamic';
import { InstagramAnonymouslyModel } from '@/types/anonymously.interface';
import { fetchInstagramAnonymouslySearchData } from '@/lib/service/anonymously';
import { addInsStory } from '@/lib/service/dashboard';
import { message } from 'antd';
import router from 'next/router';
import { useTranslation } from 'next-i18next';
import { AccountLimitAlert, LimitAlert } from '../LandingPage/FeeLimitAlert';
import { EventName } from '@/shared/event-types';
import { ga_logEvent } from '@/shared/ga';
import useMediaNameSync from '@/lib/hooks/useMediaNameSync';
import CommonSimpleModal from '@/components/Common/CommonSimpleModal';
import UserDashboard from '../Dashboard/UserDashboard';
import { InsAccountModel } from '@/types/home';
import useStoryPremium from '@/lib/hooks/isPremium/useStoryPremium';
import TurnstileModal from '@/components/TurnstileModal';
import useTurnstileVerify from '@/lib/hooks/anonymous-instagram-story-viewer/useTurnstileVerify';

// CLS 优化：骨架屏和内容组件使用同步导入
import SearchResultSkeleton from '../web-viewer-for-instagram/SearchResultSkeleton';
import SearchResultContent from '../LandingPage/SearchResultContent';
import BannerAd from './TopSearch/banner-ad';

const FAQContent = dynamic(() => import('./FAQContent'), { ssr: true });
const FeaturesContent = dynamic(() => import('./FeaturesContent'), { ssr: true });
const LandingPagePricingContent = dynamic(() => import('../LandingPage/PricingContent'), {
  ssr: true,
});
// CLS 优化：使用通用的 TopSearchContent，搜索逻辑提到父组件
const TopSearchContent = dynamic(() => import('../LandingPage/TopSearch'), { ssr: true });
const ChooseDolphinRadarContent = dynamic(() => import('./ChooseDolphinRadar'), { ssr: true });

const AnonymouslyContent = ({ data }: { data?: any }) => {
  const [currentModel, setCurrentModel] = useState<InstagramAnonymouslyModel>(null);
  const [insListLoading, setInsListLoading] = useState(false);
  const priceRef = useRef(null);
  const searchResultRef = useRef<HTMLDivElement>(null);
  const { turnstileRef, getTurnstileToken } = useTurnstileVerify();
  const { t } = useTranslation();
  const [showLimit, setShowLimit] = useState(false);
  const [showAccountLimit, setShowAccountLimit] = useState(false);

  // CLS 优化：搜索相关状态
  const [loading, setLoading] = useState(false);
  const [showContent, setShowContent] = useState(false);
  const [keepSkeletonOnError, setKeepSkeletonOnError] = useState(false);
  const [searchStr, setSearchStr, writeToStorage] = useMediaNameSync('');
  const [alertInfo, setAlertInfo] = useState<any>(null);
  const { isPremium } = useStoryPremium();

  const onAddAccountToTrackClick = (media_name?: string) => {
    if (insListLoading) {
      return;
    }
    setInsListLoading(true);

    addInsStory(media_name)
      ?.then((res) => {
        if (res?.code === 0) {
          message.success(t('Added successfully'));
          router.push('/dashboard?dashboard_type=toolkit');
        } else {
          if (res?.code !== 22008) {
            message.error(res?.message);
          }

          if (res?.code === 22007) {
            setShowLimit(true);
          } else if (res?.code === 22008) {
            setShowAccountLimit(true);
          }
        }
      })
      .finally(() => {
        setInsListLoading(false);
      });
  };

  const gotoPlanClick = () => {
    if (priceRef.current) {
      const targetTop = priceRef.current.offsetTop - 60;
      window.scrollTo({
        top: targetTop,
        behavior: 'smooth',
      });
    }
  };

  const onSearch = async (value?: string) => {
    if (loading) {
      return;
    }
    const turnstileToken = await getTurnstileToken();
    if (turnstileToken === null) {
      return;
    }
    onSearchClick(value, turnstileToken);
  };

  // CLS 优化：搜索逻辑提到父组件，与 /web-viewer-for-instagram 保持一致
  const onSearchClick = (e?: string, token?: string) => {
    if (e?.length > 0) {
      if (e.includes('/reel/') || e.includes('/reels/')) {
        setLoading(true);
        setKeepSkeletonOnError(true);
        setAlertInfo({
          title: t('Invalid Instagram Username'),
          desc: t('The username you entered is not valid. Please check the username and try again.'),
          btnTitle: t('TRYAGAIN'),
          type: 'Instagram-Account-Not-Found',
        });
        return;
      }
      writeToStorage(e);

      if (loading) {
        return;
      }
      setLoading(true);
      setShowContent(false);

      fetchInstagramAnonymouslySearchData(e, token ?? '', null, {
        isPremium,
        bizFuncName: 'stories_viewer',
      })
        .then((res) => {
          if (res?.code === 0) {
            if (res.data.private_mark) {
              let PrivateData: InsAccountModel = {
                is_private: res.data.private_mark ? 1 : 0,
                profile_pic_url: res.data.avatar,
                media_name: res.data.media_name,
              };
              setCurrentModel(res?.data);
              setLoading(false);
              setShowContent(true);
              setAlertInfo({
                title: t('OopsPrivateProfileDetected'),
                desc: (
                  <>
                    <div style={{ marginBottom: '24px', textAlign: 'center' }}>
                      {t('ThisInstagramAccountIsPrivate_Desc')}
                    </div>
                    <UserDashboard {...PrivateData} />
                  </>
                ),
                btnTitle: t('TRYAGAIN'),
                btnDesc: (
                  <p style={{ color: 'var(--app-text-30-color)', marginTop: '10px' }}>
                    {t('Note: Only public accounts can be tracked.')}
                  </p>
                ),
              });
              return;
            }

            // CLS 优化核心：所有状态更新在同一个同步函数中
            const doShowContent = () => {
              setCurrentModel(res?.data);
              setLoading(false);
              setShowContent(true);
            };

            // 先滚动到目标位置，滚动完成后再显示内容
            if (searchResultRef.current) {
              const targetTop = searchResultRef.current.offsetTop - 120;
              window.scrollTo({
                top: targetTop,
                behavior: 'smooth',
              });
              let scrollTimer: NodeJS.Timeout;
              const handleScrollEnd = () => {
                clearTimeout(scrollTimer);
                scrollTimer = setTimeout(() => {
                  window.removeEventListener('scroll', handleScrollEnd);
                  doShowContent();
                }, 100);
              };
              window.addEventListener('scroll', handleScrollEnd);
              handleScrollEnd();
            } else {
              doShowContent();
            }
          } else if (res?.code === 22001 || res?.code === 22006) {
            setShowLimit(true);
            setLoading(false);
          } else if (res?.code === 22002) {
            setKeepSkeletonOnError(true);
            setAlertInfo({
              title: t('Invalid Instagram Username'),
              desc: t('The username you entered is not valid. Please check the username and try again.'),
              btnTitle: t('TRYAGAIN'),
              type: 'Instagram-Account-Not-Found',
            });
          } else if (res?.code === 22004) {
            setSearchStr('');
            setCurrentModel(null);
            setLoading(false);
          } else {
            // CLS 优化：其他错误（如 Too many requests）也保持骨架屏显示，直到用户关闭弹窗
            setKeepSkeletonOnError(true);
            setAlertInfo({
              title: t('Warning'),
              desc: res?.message || t('An error occurred. Please try again later.'),
              btnTitle: t('OK'),
              type: 'API-Error',
            });
            // 注意：不调用 setLoading(false)，保持骨架屏显示
          }
        })
        .catch((error) => {
          // CLS 优化：网络异常也保持骨架屏显示，直到用户关闭弹窗
          setKeepSkeletonOnError(true);
          setAlertInfo({
            title: t('Network Error'),
            desc: t('Network connection failed. Please check your internet connection and try again.'),
            btnTitle: t('OK'),
            type: 'Network-Error',
          });
          // 注意：不调用 setLoading(false)，保持骨架屏显示
        });
    }
  };

  return (
    <>
      <CommonBackContent style={{ minHeight: '100vh' }}>
        <TopSearchContent
          topInfo={{
            title: t('Anonymous Instagram Story Viewer'),
            desc: t(
              'Instagram Story viewer allows anyone to watch Instagram Stories from public accounts and save them anonymously and completely free, with no app installation required. Just enter the Instagram username to explore, view, and download Stories and Highlights on any device. Try it now!'
            ),
          }}
          loading={loading}
          searchVal={searchStr}
          onSearchClick={(e) => {
            void onSearch(e);
          }}
          onValueChanged={(e) => setSearchStr(e ?? '')}
        />

        {/* CLS 优化：loading 时显示骨架屏，搜索完成后同帧切换到真实内容 */}
        <div ref={searchResultRef}>
          {(loading || keepSkeletonOnError) && !showContent ? (
            <>
              {/* 广告位：与骨架屏一起显示，避免 CLS */}
              <BannerAd />
              <SearchResultSkeleton tabFunc="story" />
            </>
          ) : showContent && currentModel ? (
            <>
              {/* 广告位：真实搜索结果时显示 */}
              <BannerAd />
              <SearchResultContent
                loading={insListLoading}
                loadMoreNotCheckLogin={true}
                post_page_token={currentModel?.post_page_token}
                data={currentModel}
                onAddAccountToTrackClick={onAddAccountToTrackClick}
                onPlanClick={() => setShowLimit(true)}
                isDemoData={false}
              />
            </>
          ) : (
            <SearchResultContent
              loading={insListLoading}
              loadMoreNotCheckLogin={true}
              post_page_token={data?.demoData?.post_page_token}
              data={data?.demoData}
              onAddAccountToTrackClick={onAddAccountToTrackClick}
              onPlanClick={() => setShowLimit(true)}
              isDemoData={true}
            />
          )}
        </div>

        <ChooseDolphinRadarContent goPlanClick={gotoPlanClick} />
        <FeaturesContent />
        <div ref={priceRef}>
          <LandingPagePricingContent func="story" />
        </div>
      </CommonBackContent>

      <FAQContent isFromHome={true} />

      <LimitAlert
        open={showLimit}
        closeAction={(isOk) => {
          setShowLimit(false);

          if (isOk) {
            gotoPlanClick();
          }
        }}
      />

      <AccountLimitAlert
        open={showAccountLimit}
        closeAction={(isOk) => {
          setShowAccountLimit(false);

          if (isOk) {
            ga_logEvent(EventName.GA_account_limit_reached_notify_clicked);
            setTimeout(() => {
              gotoPlanClick();
            }, 20);
          }
        }}
      />

      <CommonSimpleModal
        open={alertInfo !== null}
        title={alertInfo?.title}
        desc={alertInfo?.desc}
        btnTitle={alertInfo?.btnTitle}
        closeAction={(isOk) => {
          if (keepSkeletonOnError) {
            setKeepSkeletonOnError(false);
            setLoading(false);
          }

          if (isOk) {
            setAlertInfo(null);
          } else {
            setAlertInfo(null);
          }
        }}
      />
      <TurnstileModal ref={turnstileRef} />
    </>
  );
};

export default AnonymouslyContent;
